import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { LanguageProvider } from "./contexts/LanguageContext";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { initWebVitals } from "./lib/webVitals";
import { referralService } from "./services/referralService";

// Capture referral code from URL immediately (before React mounts)
referralService.captureReferralCode();

// Initialize React Query
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

// Persist query cache to localStorage for instant data on return visits
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'cashee-query-cache',
  throttleTime: 1000,
});

// Register Service Worker for PWA with update detection
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(registration => {
      console.log('[SW] Registered successfully:', registration.scope);
      
      // Check for updates periodically (every 60 seconds)
      setInterval(() => registration.update(), 60000);
      
      // Detect when new SW is installed
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New version available - dispatch custom event for UpdateNotification
            console.log('[SW] New version available');
            window.dispatchEvent(new CustomEvent('sw:update-available'));
          }
        });
      });
    }).catch(error => {
      console.log('[SW] Registration failed:', error);
    });
  });
}

// Initialize Core Web Vitals monitoring
initWebVitals();

createRoot(document.getElementById("root")!).render(
  <LanguageProvider>
    <PersistQueryClientProvider client={queryClient} persistOptions={{ persister, maxAge: 1000 * 60 * 60 * 24 }}>
      <App />
    </PersistQueryClientProvider>
  </LanguageProvider>
);
